import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AlertController, NavController } from '@ionic/angular';
import { EMPTY } from 'rxjs';
import { catchError, exhaustMap, filter, withLatestFrom } from 'rxjs/operators';
import { AuthFacade } from '../../auth/state/auth.facade';
import { environment } from '../../../environments/environment';
import { CheckinGeofenceService } from './CheckinGeofenceService';

export interface CheckinSuggestion {
	placeId: string;
	placeName: string;
}

// Suggests checking in when the user stands at a place. Runs on every fresh position: app start and,
// on native, every return to the foreground (app.component.ts's appStateChange -> updateCoordinates).
// The backend decides (150 m, no check-in in the last 6 hours) and answers 204 -> null otherwise.
// Fase 2 (CheckinGeofenceService) is offered here, next to the suggestion, and only once: with "always"
// location a geofence entry feeds this same flow, and a backgrounded app gets a notification instead.
@Injectable({
	providedIn: 'root'
})
export class CheckinSuggestionService {

	constructor(private http: HttpClient, private authFacade: AuthFacade, private alertCtrl: AlertController, private navCtrl: NavController, private geofence: CheckinGeofenceService) {
		this.authFacade.location$.pipe(
			filter(location => !!location?.latitude && !!location?.longitude),
			withLatestFrom(this.authFacade.isLocked$),
			filter(([, isLocked]) => !isLocked),
			exhaustMap(() => this.http.get<CheckinSuggestion | null>(`${environment.apiHost}checkins/suggestion`).pipe(
				catchError(() => EMPTY)
			)),
			filter(suggestion => !!suggestion)
		).subscribe(suggestion => this.suggest(suggestion));
	}

	private async suggest(suggestion: CheckinSuggestion): Promise<void> {
		if (await this.geofence.notify(suggestion)) {
			return;
		}

		const offerGeofence = await this.geofence.canOffer();
		const alert = await this.alertCtrl.create({
			header: 'Inchecken?',
			message: `Je bent bij ${suggestion.placeName}. Wil je inchecken?` + (offerGeofence
				? ' Wil je deze vraag ook krijgen zonder de app te openen? Dan vraagt je telefoon om locatie "altijd" toe te staan - alleen om te zien dat je bij een poolcentrum bent.'
				: ''),
			buttons: [
				{ text: 'Niet nu', role: 'cancel' },
				...(offerGeofence ? [{ text: 'Ook zonder app', handler: () => { this.geofence.enable(); } }] : []),
				{
					text: 'Inchecken',
					handler: () => {
						this.navCtrl.navigateForward(`tabs/places/detail/${suggestion.placeId}`, { queryParams: { checkin: true } });
					}
				}
			]
		});
		await alert.present();
	}
}
